import {
  ApplicationRef,
  Component,
  HostBinding,
  HostListener,
  Inject,
  OnDestroy,
  OnInit,
}                                 from '@angular/core';
import { TranslateService }       from '@ngx-translate/core';
import {
  first,
  interval,
  map,
  Subject,
  takeUntil,
}                                 from 'rxjs';
import packageJson                from '../../package.json';
import {
  ENVIRONMENT,
  LOCATION,
}                                 from './app.constants';
import {
  ColorModeService,
  RbAuthService,
  UpdateService,
}                                 from './core';
import { MaintenanceModeService } from './maintenance-mode/maintenance-mode.service';
import { ReportEventsService }    from './report-events/report-events.service';

@Component({
  selector: 'rb-root',
  templateUrl: './app.component.html',
  standalone: false,
})
export class AppComponent implements OnInit, OnDestroy {
  @HostBinding('attr.app-version')
  readonly appVersion = packageJson.version;
  authIsLoading$ = this.rbAuthService.isLoading$;
  darkModeEnabled$ = this.colorModeService.colorMode$.pipe(
    map(colorMode => colorMode === 'dark' || (!colorMode && window.matchMedia('(prefers-color-scheme: dark)').matches)),
  );
  maintenanceMode$ = this.maintenanceModeService.maintenanceMode$;
  private destroy$ = new Subject<void>();

  constructor(
    @Inject(ENVIRONMENT) private readonly environment: Environment,
    @Inject(LOCATION) private readonly location: Location,
    private readonly applicationRef: ApplicationRef,
    private readonly rbAuthService: RbAuthService,
    private readonly reportEventsService: ReportEventsService,
    private readonly colorModeService: ColorModeService,
    private readonly maintenanceModeService: MaintenanceModeService,
    private readonly translateService: TranslateService,
    private readonly updateService: UpdateService,
  ) {
  }

  ngOnInit() {
    if (this.environment.production) {
      if (!navigator.serviceWorker.controller) {
        // page loaded before SW was registered, can only happen on hard refresh
        // do a soft refresh as soon as the controllerchange event occurs to have the SW control the page
        navigator.serviceWorker.addEventListener('controllerchange', () => {
          this.location.reload();
        });
      }
      navigator.serviceWorker.ready.then(reg => {
        if (reg.active) {
          // detect if the SW is in safe mode and force page reload if so
          reg.active.postMessage({ action: 'CHECK_FOR_SAFE_MODE' });
        }
      });
    }

    this.rbAuthService.isAuthenticated$
      .pipe(takeUntil(this.destroy$))
      .subscribe(authenticated => {
        if (authenticated) {
          this.reportEventsService.start();
        } else {
          this.reportEventsService.stop();
        }
      });

    this.colorModeService.fetchColorMode();
    this.updateService.checkForUpdate();
    this.setHtmlLangAttribute(this.translateService.currentLang);
    this.translateService.onLangChange
      .pipe(takeUntil(this.destroy$))
      .subscribe(event => {
        this.setHtmlLangAttribute(event.lang);
      });
    this.applicationRef.isStable
      .pipe(first((isStable) => isStable))
      .subscribe(() => {
        this.pollForMaintenanceMode();
      });
  }

  ngOnDestroy() {
    this.reportEventsService.stop();
    this.destroy$.next();
    this.destroy$.complete();
  }

  @HostListener('document:visibilitychange', ['$event'])
  onVisibilityChange(event: Event) {
    const { visibilityState } = event.target as Document;
    if (visibilityState === 'visible') {
      this.updateService.checkForUpdate();
      this.maintenanceModeService.fetchMaintenanceMode();
    }
  }

  private pollForMaintenanceMode() {
    this.maintenanceModeService.fetchMaintenanceMode();
    interval(60000) // every minute
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.maintenanceModeService.fetchMaintenanceMode());
  }

  private setHtmlLangAttribute(lang: string): void {
    document.documentElement.setAttribute('lang', lang);
  }
}
